import { useMemo, type FC, type MouseEvent, type PropsWithChildren, type ReactNode } from 'react';
import type { Middleware } from '@floating-ui/core';
import { offset } from '@floating-ui/core';
import { useFloating, useFocus, useInteractions, useClick, useDismiss, flip, autoUpdate, size } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Icons from '../../../../Elements/Icons/index.ts';
import { ANIMATION_DURATION_MS } from './constants.ts';
import { Button, ButtonWrapper, Wrapper, CaretWrapper } from './styled.ts';

export interface FilterButtonProps extends PropsWithChildren {
  id?: string;
  className?: string;
  dataTestId?: string;
  disabled?: boolean;
  dropdown: ReactNode;
  zIndex?: number;
  align?: 'left' | 'right';
  isShown: boolean;
  setIsShown: (state: boolean) => void;
  recover?: ReactNode | null;
  dropdownWidth: number;
}

function selectPlacement(align: 'left' | 'right') {
  switch (align) {
    case 'right':
      return 'bottom-start';

    case 'left':
      return 'bottom-end';

    default:
      return 'bottom-start';
  }
}

const marginFromBottomScreenEdge = 8;
const maxMenuHeight = 375;
export const FilterButton: FC<FilterButtonProps> = ({
  id,
  className,
  dataTestId,
  children,
  dropdown,
  disabled = false,
  zIndex = 8,
  align = 'right',
  isShown,
  setIsShown,
  recover,
  dropdownWidth,
}) => {
  const theme = useTheme();

  const floatingPlacement = useMemo(() => selectPlacement(align), [align]);

  const middleware = useMemo<Array<Middleware | null | undefined | false>>(
    () => [
      offset(5),
      flip({
        fallbackPlacements: ['bottom-end', 'bottom-start', 'top-end', 'top-start'],
      }),
      size({
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight =
            availableHeight < maxMenuHeight ? `${availableHeight - marginFromBottomScreenEdge}px` : `${maxMenuHeight}px`;
        },
      }),
    ],
    [],
  );

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isShown,
    onOpenChange: setIsShown,
    whileElementsMounted: autoUpdate,
    placement: floatingPlacement,
    middleware,
  });

  const focus = useFocus(context);
  const click = useClick(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus]);

  useDismiss(context, {
    enabled: isShown,
  });

  const isTop = useMemo(() => placement.startsWith('top'), [placement]);

  const stopPropagation = useCallbackRef((evt: MouseEvent) => evt.stopPropagation());

  const caretColor = useMemo(
    () => (disabled ? theme.colors.grey30_1 : theme.colors.grey70_1),
    [disabled, theme.colors.grey30_1, theme.colors.grey70_1],
  );

  const memoizedFloatingStyles = useMemo(
    () => ({
      ...floatingStyles,
      zIndex,
    }),
    [floatingStyles, zIndex],
  );

  return (
    <>
      {ReactDOM.createPortal(
        <CSSTransition in={isShown} transitionAppear timeout={ANIMATION_DURATION_MS} unmountOnExit>
          <Wrapper
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={memoizedFloatingStyles}
            $visible={isShown}
            $isTop={isTop}
            width={dropdownWidth}
            data-testid='filter-content-wrapper'>
            {dropdown}
          </Wrapper>
        </CSSTransition>,
        document.body,
      )}
      <ButtonWrapper onClick={stopPropagation}>
        {recover ? (
          <Button className={className} type='button'>
            {children}
            {recover}
          </Button>
        ) : (
          <Button
            id={id}
            className={className}
            data-testid={dataTestId}
            disabled={disabled}
            // eslint-disable-next-line react-hooks/refs
            ref={refs.setReference}
            {...getReferenceProps()}
            type='button'>
            {children}
            <CaretWrapper>
              <Icons.Caret size={16} align={isShown ? 'top' : 'bottom'} color={caretColor} />
            </CaretWrapper>
          </Button>
        )}
      </ButtonWrapper>
    </>
  );
};
